import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "jm_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const matchKey = (i) => i.product_id === product.product_id && (i.variant_vid || null) === (product.variant_vid || null);
      const existing = prev.find(matchKey);
      const cap = product.max_qty ?? existing?.max_qty ?? 99;
      if (existing) {
        return prev.map((i) =>
          matchKey(i)
            ? { ...i, ...product, quantity: Math.min(cap, i.quantity + quantity) }
            : i
        );
      }
      return [...prev, { ...product, quantity: Math.min(cap, quantity) }];
    });
  }, []);

  const updateQuantity = useCallback((productId, quantity, variantVid = undefined) => {
    setItems((prev) =>
      prev
        .map((i) => (i.product_id === productId && (variantVid === undefined || (i.variant_vid || null) === (variantVid || null))
          ? { ...i, quantity: Math.min(i.max_qty ?? 99, Math.max(1, quantity)) }
          : i))
        .filter((i) => i.quantity > 0)
    );
  }, []);

  const removeItem = useCallback((productId, variantVid = undefined) => {
    setItems((prev) => prev.filter((i) => !(i.product_id === productId && (variantVid === undefined || (i.variant_vid || null) === (variantVid || null)))));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);

  const value = useMemo(
    () => ({ items, addItem, updateQuantity, removeItem, clearCart, count, subtotal }),
    [items, addItem, updateQuantity, removeItem, clearCart, count, subtotal]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
